import React, { useState, useEffect, useMemo, useRef } from "react"
import { useLocation } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { dashboardApi, coursesApi, termsApi, assignmentsApi, lmsConnectionsApi } from "../../lib/api"
import StatsBar from "./StatsBar"
import AssignmentSection from "./AssignmentSection"
import CourseSidebar from "./CourseSidebar"
import ManualAssignmentForm from "../assignments/ManualAssignmentForm"
import ManualCourseForm from "../courses/ManualCourseForm"
import AddLmsConnectionForm from "../settings/AddLmsConnectionForm"

const Dashboard = () => {
  const { user, updateUser } = useAuth()
  const location = useLocation()
  const [data, setData] = useState(null)
  const [courses, setCourses] = useState([])
  const [terms, setTerms] = useState([])
  const [currentTermId, setCurrentTermId] = useState(null)
  const [selectedTerm, setSelectedTerm] = useState(null)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [selectedStatFilter, setSelectedStatFilter] = useState(null)
  const [showCompleted, setShowCompleted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [showAddAssignment, setShowAddAssignment] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [showConnectLms, setShowConnectLms] = useState(false)
  const [showAddCourse, setShowAddCourse] = useState(false)
  const [courseFormLoading, setCourseFormLoading] = useState(false)
  const [syncingConnectionId, setSyncingConnectionId] = useState(null)
  const syncPollingRef = useRef(null)

  useEffect(() => {
    fetchData()
  }, [user, location.key])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [dashboardData, coursesData, termsData] = await Promise.all([
        dashboardApi.get(),
        coursesApi.list(),
        termsApi.list(),
      ])
      setData(dashboardData)
      setCourses(coursesData.courses)
      setTerms(termsData.terms)
      setCurrentTermId(termsData.current_term_id)
      if (selectedTerm === null && termsData.current_term_id) {
        setSelectedTerm(termsData.current_term_id)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const listedCourseIds = useMemo(
    () => new Set(courses.filter((c) => !c.hidden).map((c) => c.id)),
    [courses]
  )

  const filterByListedCourses = (assignments) =>
    assignments.filter((a) => listedCourseIds.has(a.course.id))

  const filterByTerm = (assignments) => {
    if (selectedTerm === null) return assignments
    return assignments.filter((a) => a.course.term_id === selectedTerm)
  }

  const filterByCourse = (assignments) => {
    if (!selectedCourse) return assignments
    return assignments.filter((a) => a.course.id === selectedCourse)
  }

  const applyFilters = (assignments) => {
    return filterByCourse(filterByTerm(filterByListedCourses(assignments)))
  }

  // Remove a single assignment from local state (used after completion animation)
  const removeAssignmentFromList = (assignmentId) => {
    if (!data) return

    setData((prevData) => {
      const newAssignments = {}
      let removedAssignment = null

      // Find and remove the assignment from whichever section it's in
      for (const [section, assignments] of Object.entries(prevData.assignments)) {
        const filtered = assignments.filter((a) => {
          if (a.id === assignmentId) {
            removedAssignment = a
            return false
          }
          return true
        })
        newAssignments[section] = filtered
      }

      // Add to completed section if we found and removed it
      if (removedAssignment) {
        removedAssignment.manually_completed = true
        removedAssignment.manually_completed_at = new Date().toISOString()
        newAssignments.completed = [removedAssignment, ...(newAssignments.completed || [])]
      }

      return {
        ...prevData,
        assignments: newAssignments,
        stats: {
          ...prevData.stats,
          completed: (prevData.stats.completed || 0) + 1,
        },
      }
    })
  }

  // Handler for adding a new manual assignment
  const handleAddAssignment = async (formData) => {
    setFormLoading(true)
    try {
      await assignmentsApi.create(formData.course_id, {
        name: formData.name,
        description: formData.description,
        due_at: formData.due_at,
        points_possible: formData.points_possible,
        html_url: formData.html_url,
      })
      setShowAddAssignment(false)
      fetchData() // Refresh to get the new assignment in the correct section
    } catch (error) {
      console.error("Failed to add assignment:", error)
    } finally {
      setFormLoading(false)
    }
  }

  // Handler for editing a manual assignment
  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment)
  }

  // Handler for updating a manual assignment
  const handleUpdateAssignment = async (formData) => {
    if (!editingAssignment) return
    setFormLoading(true)
    try {
      await assignmentsApi.update(editingAssignment.id, {
        name: formData.name,
        description: formData.description,
        due_at: formData.due_at,
        points_possible: formData.points_possible,
        html_url: formData.html_url,
      })
      setEditingAssignment(null)
      fetchData() // Refresh to get the updated assignment in the correct section
    } catch (error) {
      console.error("Failed to update assignment:", error)
    } finally {
      setFormLoading(false)
    }
  }

  // Handler for deleting a manual assignment
  const handleDeleteAssignment = (assignmentId) => {
    // Remove assignment from all sections in local state
    setData((prevData) => {
      if (!prevData) return prevData
      const newAssignments = {}
      for (const [section, sectionAssignments] of Object.entries(prevData.assignments)) {
        newAssignments[section] = sectionAssignments.filter((a) => a.id !== assignmentId)
      }
      return { ...prevData, assignments: newAssignments }
    })
  }

  const filteredCourses = selectedTerm === null
    ? courses
    : courses.filter((c) => c.term?.id === selectedTerm)

  // Calculate per-course assignment counts
  const courseAssignmentCounts = useMemo(() => {
    if (!data?.assignments) return {}
    const counts = {}
    Object.values(data.assignments).forEach(sectionAssignments => {
      sectionAssignments.forEach(a => {
        if (!counts[a.course.id]) {
          counts[a.course.id] = { total: 0, overdue: 0 }
        }
        counts[a.course.id].total++
        if (a.urgency === 'overdue') {
          counts[a.course.id].overdue++
        }
      })
    })
    return counts
  }, [data?.assignments])

  useEffect(() => {
    setSelectedCourse(null)
  }, [selectedTerm])

  // Get current term name
  const currentTermName = useMemo(() => {
    if (!selectedTerm) return "All Semesters"
    const term = terms.find(t => t.id === selectedTerm)
    return term?.name || "All Semesters"
  }, [selectedTerm, terms])

  // Get selected course name
  const selectedCourseName = useMemo(() => {
    if (!selectedCourse) return null
    const course = courses.find(c => c.id === selectedCourse)
    return course?.course_code || course?.name || null
  }, [selectedCourse, courses])

  // Calculate filtered stats based on selected term and course
  const filteredStats = useMemo(() => {
    if (!data?.assignments) return {}
    const assignments = data.assignments

    // Inline filtering to ensure we use current selectedTerm and selectedCourse values
    const filterAssignments = (list) => {
      let result = (list || []).filter((a) => listedCourseIds.has(a.course.id))
      if (selectedTerm !== null) {
        result = result.filter((a) => a.course.term_id === selectedTerm)
      }
      if (selectedCourse) {
        result = result.filter((a) => a.course.id === selectedCourse)
      }
      return result
    }

    const filteredOverdue = filterAssignments(assignments.overdue)
    const filteredToday = filterAssignments(assignments.today)
    const filteredTomorrow = filterAssignments(assignments.tomorrow)
    const filteredThisWeek = filterAssignments(assignments.this_week)
    const filteredSubmitted = filterAssignments(assignments.submitted)
    const filteredCompleted = filterAssignments(assignments.completed)

    return {
      overdue: filteredOverdue.length,
      today: filteredToday.length,
      this_week: filteredToday.length + filteredTomorrow.length + filteredThisWeek.length,
      submitted: filteredSubmitted.length,
      completed: filteredCompleted.length
    }
  }, [data?.assignments, selectedTerm, selectedCourse, listedCourseIds])

  const handleLmsConnectionSuccess = (connection) => {
    setShowConnectLms(false)
    updateUser({ lms_configured: true })
    setSyncingConnectionId(connection.id)
    startSyncPolling(connection.id)
  }

  const handleAddCourse = async (formData) => {
    setCourseFormLoading(true)
    try {
      await coursesApi.create(formData)
      setShowAddCourse(false)
      fetchData()
    } catch (error) {
      console.error("Failed to add course:", error)
    } finally {
      setCourseFormLoading(false)
    }
  }

  const startSyncPolling = (connectionId) => {
    // Clear any existing polling
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current)
    }

    syncPollingRef.current = setInterval(async () => {
      try {
        const data = await lmsConnectionsApi.get(connectionId)
        if (!data.connection.syncing) {
          // Sync complete - stop polling and refresh data
          clearInterval(syncPollingRef.current)
          syncPollingRef.current = null
          setSyncingConnectionId(null)
          fetchData()
        }
      } catch (error) {
        console.error("Failed to poll sync status:", error)
        clearInterval(syncPollingRef.current)
        syncPollingRef.current = null
        setSyncingConnectionId(null)
      }
    }, 2000)
  }

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (syncPollingRef.current) {
        clearInterval(syncPollingRef.current)
      }
    }
  }, [])


  // Show syncing state during initial sync (takes priority over loading skeleton)
  if (syncingConnectionId) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
            <svg className="w-10 h-10 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="font-display text-2xl font-semibold text-[var(--ink)] mb-3">Syncing Your Courses</h3>
          <p className="text-[var(--ink-muted)] leading-relaxed">
            We're importing your courses and assignments. This usually takes less than a minute.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-indigo-600">
            <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            <span>Sync in progress...</span>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex gap-6">
        {/* Sidebar skeleton */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="animate-pulse space-y-3">
            <div className="h-10 bg-[var(--cream)] rounded-xl"></div>
            <div className="h-12 bg-[var(--cream)] rounded-xl"></div>
            <div className="h-12 bg-[var(--cream)] rounded-xl"></div>
            <div className="h-12 bg-[var(--cream)] rounded-xl"></div>
          </div>
        </div>

        {/* Main content skeleton */}
        <div className="flex-1 space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse h-24 bg-[var(--cream)] rounded-2xl"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse h-20 bg-[var(--cream)] rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-[var(--ink-muted)] mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-5 py-2.5 bg-[var(--ink)] text-white rounded-xl hover:bg-[var(--ink-muted)] transition-colors font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  const assignments = data?.assignments || {}
  const stats = data?.stats || {}

  const hasAnyAssignments = Object.values(assignments).some(
    (section) => section.length > 0
  )

  // No courses at all - show getting started screen
  if (courses.length === 0) {
    return (
      <>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="font-display text-2xl font-semibold text-[var(--ink)] mb-3">Get Started</h3>
            <p className="text-[var(--ink-muted)] mb-8 leading-relaxed">
              Connect your learning management system or add courses manually to start organizing your assignments.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowConnectLms(true)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[var(--ink)] text-white rounded-xl hover:bg-[var(--ink-muted)] transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Connect LMS
              </button>
              <button
                onClick={() => setShowAddCourse(true)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[var(--cream)] text-[var(--ink)] rounded-xl hover:bg-[var(--paper-warm)] transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Courses Manually
              </button>
            </div>
          </div>
        </div>

        {showConnectLms && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Connect Your LMS</h2>
                  <button
                    onClick={() => setShowConnectLms(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <AddLmsConnectionForm
                  onSuccess={handleLmsConnectionSuccess}
                  onCancel={() => setShowConnectLms(false)}
                />
              </div>
            </div>
          </div>
        )}

        {showAddCourse && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Add Course</h2>
                  <button
                    onClick={() => setShowAddCourse(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <ManualCourseForm
                  onSubmit={handleAddCourse}
                  onCancel={() => setShowAddCourse(false)}
                  isLoading={courseFormLoading}
                />
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // Has courses but no assignments
  if (!hasAnyAssignments) {
    return (
      <>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
              <svg className="w-10 h-10 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="font-display text-2xl font-semibold text-[var(--ink)] mb-3">All Caught Up!</h3>
            <p className="text-[var(--ink-muted)] mb-6 leading-relaxed">
              No assignments yet. Add one to get started!
            </p>
            <button
              onClick={() => setShowAddAssignment(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Assignment
            </button>
          </div>
        </div>

        {showAddAssignment && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">Add Assignment</h2>
                <button
                  onClick={() => setShowAddAssignment(false)}
                  className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form Content - scrollable with max height */}
              <div className="px-6 pt-5 pb-6 max-h-[calc(90vh-80px)] overflow-y-auto">
                <ManualAssignmentForm
                  courses={filteredCourses.filter(c => !c.hidden)}
                  onSubmit={handleAddAssignment}
                  onCancel={() => setShowAddAssignment(false)}
                  isLoading={formLoading}
                />
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="flex gap-6">
      {/* Course Sidebar */}
      <CourseSidebar
        courses={filteredCourses}
        terms={terms}
        selectedTerm={selectedTerm}
        currentTermId={currentTermId}
        selectedCourse={selectedCourse}
        onSelectTerm={setSelectedTerm}
        onSelectCourse={setSelectedCourse}
        courseAssignmentCounts={courseAssignmentCounts}
        mobileOpen={mobileSidebarOpen}
        onMobileOpenChange={setMobileSidebarOpen}
      />

      {/* Main Content */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Header with context */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile menu trigger */}
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden flex-shrink-0 p-2 -ml-2 hover:bg-[var(--cream)] rounded-xl transition-colors"
              aria-label="Open courses menu"
            >
              <svg className="w-5 h-5 text-[var(--ink-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            </button>

            <div className="min-w-0">
              <h1 className="font-display text-xl sm:text-2xl font-semibold text-[var(--ink)] truncate">
                {selectedCourseName || "All Assignments"}
              </h1>
              <p className="text-sm text-[var(--ink-light)] mt-0.5">{currentTermName}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddAssignment(true)}
              className="flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Add</span>
            </button>
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className={`flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                showCompleted
                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                  : "bg-[var(--cream)] text-[var(--ink-muted)] hover:bg-[var(--paper-warm)]"
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="hidden sm:inline">{showCompleted ? "Hide" : "Show"} Completed</span>
              {filteredStats.completed > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  showCompleted ? "bg-emerald-200 text-emerald-800" : "bg-[var(--paper)] text-[var(--ink-light)]"
                }`}>
                  {filteredStats.completed}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Add/Edit Assignment Modal */}
        {(showAddAssignment || editingAssignment) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingAssignment ? "Edit Assignment" : "Add Assignment"}
                </h2>
                <button
                  onClick={() => {
                    setShowAddAssignment(false)
                    setEditingAssignment(null)
                  }}
                  className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form Content - scrollable with max height */}
              <div className="px-6 pt-5 pb-6 max-h-[calc(90vh-80px)] overflow-y-auto">
                <ManualAssignmentForm
                  assignment={editingAssignment}
                  courses={filteredCourses.filter(c => !c.hidden)}
                  onSubmit={editingAssignment ? handleUpdateAssignment : handleAddAssignment}
                  onCancel={() => {
                    setShowAddAssignment(false)
                    setEditingAssignment(null)
                  }}
                  isLoading={formLoading}
                />
              </div>
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <StatsBar
          stats={filteredStats}
          activeFilter={selectedStatFilter}
          onFilterClick={setSelectedStatFilter}
        />

        {/* Active filter indicator */}
        {selectedStatFilter && (
          <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)] bg-[var(--cream)] rounded-lg px-3 py-2">
            <span>Filtering by:</span>
            <span className="font-semibold text-[var(--ink)] capitalize">
              {selectedStatFilter === "this_week" ? "This Week" : selectedStatFilter}
            </span>
            <button
              onClick={() => setSelectedStatFilter(null)}
              className="ml-auto p-1 hover:bg-[var(--paper-warm)] rounded-md transition-colors"
              aria-label="Clear filter"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Assignment Sections */}
        <div className="space-y-2">
          {(!selectedStatFilter || selectedStatFilter === "overdue") && (
            <AssignmentSection
              title="Past Due"
              assignments={applyFilters(assignments.overdue || [])}
              urgency="overdue"
              emptyMessage="Nothing overdue"
              collapsed
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {!selectedStatFilter && (
            <AssignmentSection
              title="Due Soon"
              assignments={applyFilters(assignments.due_soon || [])}
              urgency="due_soon"
              emptyMessage="Nothing due in the next 48 hours"
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {(!selectedStatFilter || selectedStatFilter === "today") && (
            <AssignmentSection
              title="Due Today"
              assignments={applyFilters(assignments.today || [])}
              urgency="today"
              emptyMessage="Nothing due today"
              collapsed
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {!selectedStatFilter && (
            <AssignmentSection
              title="Due Tomorrow"
              assignments={applyFilters(assignments.tomorrow || [])}
              urgency="tomorrow"
              emptyMessage="Nothing due tomorrow"
              collapsed
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {(!selectedStatFilter || selectedStatFilter === "this_week") && (
            <AssignmentSection
              title="Due This Week"
              assignments={applyFilters(assignments.this_week || [])}
              urgency="this_week"
              emptyMessage="Nothing due this week"
              collapsed
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {!selectedStatFilter && (
            <AssignmentSection
              title="Due Next Week"
              assignments={applyFilters(assignments.next_week || [])}
              urgency="next_week"
              emptyMessage="Nothing due next week"
              collapsed
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {!selectedStatFilter && (
            <AssignmentSection
              title="Coming Up"
              assignments={applyFilters(assignments.later || [])}
              urgency="later"
              collapsed
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {!selectedStatFilter && (
            <AssignmentSection
              title="No Due Date"
              assignments={applyFilters(assignments.no_due_date || [])}
              urgency="none"
              collapsed
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {(!selectedStatFilter || selectedStatFilter === "submitted") && (
            <AssignmentSection
              title="Submitted"
              assignments={applyFilters(assignments.submitted || [])}
              urgency="submitted"
              collapsed={!selectedStatFilter}
              emptyMessage="No submitted assignments in this view"
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}

          {showCompleted && (
            <AssignmentSection
              title="Completed"
              assignments={applyFilters(assignments.completed || [])}
              urgency="completed"
              emptyMessage="No completed assignments yet"
              collapsed
              onRefresh={fetchData}
              onRemoveAssignment={removeAssignmentFromList}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
